import { useMemo } from "react";
import { useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import {
  orderEventStyles,
  resolveDanceStyles,
  type DanceStyleCategoryRow,
  type DanceStyleRow,
} from "convex/danceStyleDefaults";

type StyleList = { styles: DanceStyleRow[]; categories: DanceStyleCategoryRow[] };

/**
 * The site's dance styles and their categories, admin-managed in Convex.
 *
 * Shows the built-in defaults while the query is in flight and whenever the
 * table is empty, so the calendar, filters, badges and map never render blank —
 * on a fresh deployment, or before a moderator has imported the defaults.
 * Every component asking for this shares one Convex subscription.
 */
export function useDanceStyles() {
  const data = useQuery(api.danceStyles.list) as StyleList | undefined;

  return useMemo(() => {
    const resolved = resolveDanceStyles(data?.styles, data?.categories);
    return {
      ...resolved,
      /** An event's styles in display order: main styles, then list order. */
      orderStyles: (danceStyles: readonly string[] | undefined, mainStyles?: readonly string[]) =>
        orderEventStyles(danceStyles, mainStyles, resolved.rank),
      /** True while loading — the defaults are being shown. */
      isLoading: data === undefined,
    };
  }, [data]);
}
